import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

import { fetchOrgAddressBooks, deleteOrgAddressBook } from "js/Api/address_book";
import { generateContactInvite } from "js/Api/contact_invite";

import OrgAddressBookDetail from "./OrgAddressBookDetail";
import AddBookModal from "./AddBookModal";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// The system-default book acts as the org's shared "contact pool". It is never
// shown in the address-book list — users interact with it as a plain contact list.
function isDefaultBook(b) {
    return b?.bookType === "system_default" || b?.systemKey === "default";
}

// ---------------------------------------------------------------------------
// Styles (scoped objects)
// ---------------------------------------------------------------------------

const S = {
    tabs: {
        display: "flex",
        gap: 4,
        borderBottom: "1px solid #e8eaf0",
        marginBottom: 24,
    },
    tab: (active) => ({
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "10px 18px",
        fontSize: 14,
        fontWeight: 600,
        color: active ? "#1976d2" : "#777",
        background: "none",
        border: "none",
        borderBottom: `2px solid ${active ? "#1976d2" : "transparent"}`,
        cursor: "pointer",
        transition: "color 0.15s, border-color 0.15s",
        marginBottom: -1,
    }),
    toolbar: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 24,
        flexWrap: "wrap",
    },
    searchWrap: {
        position: "relative",
        flex: "1 1 220px",
        maxWidth: 320,
        marginLeft: "auto",
    },
    searchIcon: {
        position: "absolute",
        left: 11,
        top: "50%",
        transform: "translateY(-50%)",
        color: "#aaa",
        fontSize: 17,
        pointerEvents: "none",
    },
    searchInput: {
        width: "100%",
        paddingLeft: 34,
        paddingRight: 12,
        paddingTop: 8,
        paddingBottom: 8,
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        fontSize: 14,
        outline: "none",
        background: "#fafafa",
        color: "#333",
        transition: "border-color 0.15s",
    },
    addBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 16,
    },
    card: {
        background: "#fff",
        border: "1.5px solid #e8eaf0",
        borderRadius: 12,
        padding: "20px 22px",
        cursor: "pointer",
        transition: "box-shadow 0.15s, border-color 0.15s",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 10,
    },
    cardIconWrap: {
        width: 44,
        height: 44,
        borderRadius: 10,
        background: "#e8f0fe",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 2,
    },
    cardName: {
        fontWeight: 700,
        fontSize: 16,
        color: "#1a1a2e",
        lineHeight: 1.3,
    },
    cardDeleteBtn: {
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 5,
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        transition: "background 0.15s",
    },
    emptyState: {
        textAlign: "center",
        padding: "60px 20px",
        color: "#aaa",
    },
    overlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    deleteModal: {
        background: "#fff",
        borderRadius: 14,
        width: 420,
        maxWidth: "90vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        overflow: "hidden",
    },
    deleteModalHeader: {
        padding: "20px 24px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    deleteModalTitle: {
        fontSize: 18,
        fontWeight: 700,
        color: "#1a1a2e",
        margin: 0,
    },
    deleteModalClose: {
        background: "none",
        border: "none",
        fontSize: 22,
        color: "#aaa",
        cursor: "pointer",
        lineHeight: 1,
        padding: 0,
    },
    deleteModalBody: {
        padding: "16px 24px 20px",
    },
    deleteModalBodyText: {
        fontSize: 14,
        color: "#444",
        marginBottom: 8,
    },
    deleteModalWarn: {
        fontSize: 13,
        color: "#b91c1c",
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: 8,
        padding: "10px 14px",
    },
    deleteModalFooter: {
        padding: "0 24px 24px",
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
    },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function OrgAddressBooks({ org }) {
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("contacts"); // "contacts" | "books"
    const [selectedBook, setSelectedBook] = useState(null);  // drilled-in custom book
    const [searchQuery, setSearchQuery] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [deletingBook, setDeletingBook] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [hoveredCard, setHoveredCard] = useState(null);

    // POC: QR/link to let family add contacts into this org's pool.
    const [inviteUrl, setInviteUrl] = useState("");
    const [inviteLoading, setInviteLoading] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);

    const handleGenerateInvite = async () => {
        if (!org?.id) return;
        setShowInviteModal(true);
        setInviteLoading(true);
        setInviteUrl("");
        const res = await generateContactInvite(org.id);
        setInviteLoading(false);
        if (res.success && res.data?.path) {
            setInviteUrl(`${window.location.origin}${res.data.path}`);
        } else {
            toast.error(res.error || "Failed to generate link");
            setShowInviteModal(false);
        }
    };

    const loadBooks = useCallback(async () => {
        if (!org?.id) return;
        setLoading(true);
        try {
            const res = await fetchOrgAddressBooks(org.id);
            if (res.success) setBooks(Array.isArray(res.data) ? res.data : []);
            else toast.error(res.error || "Failed to load address books");
        } finally {
            setLoading(false);
        }
    }, [org?.id]);

    useEffect(() => { loadBooks(); }, [loadBooks]);

    // The default book powers the "Contacts" pool tab and is hidden from the list.
    const defaultBook = useMemo(() => books.find(isDefaultBook) || null, [books]);
    const customBooks = useMemo(() => books.filter((b) => !isDefaultBook(b)), [books]);

    const handleBack = () => { setSelectedBook(null); loadBooks(); };

    const handleBookCreated = (newBook) => {
        setBooks((prev) => [...prev, newBook]);
        setActiveTab("books");
        setSelectedBook(newBook);
    };

    const handleConfirmDelete = async () => {
        if (!deletingBook) return;
        setDeleting(true);
        const res = await deleteOrgAddressBook(deletingBook.id);
        setDeleting(false);
        if (res.success) {
            toast.success("Address book deleted");
            setBooks((prev) => prev.filter((b) => b.id !== deletingBook.id));
            if (selectedBook?.id === deletingBook.id) setSelectedBook(null);
            setDeletingBook(null);
        } else {
            toast.error(res.error || "Failed to delete address book");
        }
    };

    const filteredBooks = customBooks.filter((b) =>
        (b.name || "").toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ── Drilled-in custom book detail view ───────────────────────────────────
    if (selectedBook) {
        return (
            <div className="midde_cont">
                <div className="container-fluid" style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <div className="row column_title">
                        <div className="col-md-12">
                            <div className="page_title" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <ArrowBackRoundedIcon
                                    onClick={handleBack}
                                    sx={{ fontSize: "2rem", color: "#555", cursor: "pointer", "&:hover": { color: "#000" } }}
                                />
                                <h2 style={{ margin: 0, lineHeight: 1.2 }}>{selectedBook.name}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="white_shd full margin_bottom_30">
                        <div className="full inner_elements">
                            <OrgAddressBookDetail
                                book={selectedBook}
                                allBooks={books}
                                org={org}
                                onBookRenamed={(updated) => {
                                    setSelectedBook(updated);
                                    setBooks((prev) => prev.map((b) => b.id === updated.id ? { ...b, ...updated } : b));
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Main view (tabbed: Contacts pool + Address Books list) ───────────────
    return (
        <div className="midde_cont">
            <div className="container-fluid" style={{ paddingLeft: 0, paddingRight: 0 }}>
                <div className="row column_title">
                    <div className="col-md-12">
                        <div
                            className="page_title"
                            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                        >
                            <h2>Address Book</h2>
                            <button
                                type="button"
                                className="btn btn-outline-dark"
                                onClick={handleGenerateInvite}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                Get add-contacts link
                            </button>
                        </div>
                    </div>
                </div>

                <div className="white_shd full margin_bottom_30">
                    <div className="full inner_elements" style={{ padding: "24px 28px" }}>

                        {/* Tabs */}
                        <div style={S.tabs}>
                            <button
                                type="button"
                                style={S.tab(activeTab === "contacts")}
                                onClick={() => setActiveTab("contacts")}
                            >
                                <ContactPhoneOutlinedIcon sx={{ fontSize: "1.15rem" }} />
                                Contacts
                            </button>
                            <button
                                type="button"
                                style={S.tab(activeTab === "books")}
                                onClick={() => setActiveTab("books")}
                            >
                                <LibraryBooksOutlinedIcon sx={{ fontSize: "1.15rem" }} />
                                Address Books
                            </button>
                        </div>

                        {/* ── Contacts tab — the default/pool book rendered as a plain list ── */}
                        {activeTab === "contacts" && (
                            loading && !defaultBook ? (
                                <div style={{ textAlign: "center", padding: "60px 0", color: "#aaa" }}>
                                    <i className="fa fa-spinner fa-spin" style={{ fontSize: 28, color: "#1976d2" }} />
                                    <p style={{ marginTop: 12 }}>Loading contacts…</p>
                                </div>
                            ) : defaultBook ? (
                                <OrgAddressBookDetail
                                    book={defaultBook}
                                    allBooks={books}
                                    org={org}
                                    onBookRenamed={() => { }}
                                />
                            ) : (
                                <div style={S.emptyState}>
                                    <PeopleAltOutlinedIcon sx={{ fontSize: "3.5rem", color: "#ddd", display: "block", margin: "0 auto 12px" }} />
                                    <p style={{ fontSize: 15 }}>No contact list found for this organisation.</p>
                                </div>
                            )
                        )}

                        {/* ── Address Books tab — custom books only (default excluded) ── */}
                        {activeTab === "books" && (
                            <>
                                <div style={S.toolbar}>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        style={S.addBtn}
                                        onClick={() => setShowAddModal(true)}
                                    >
                                        <AddIcon sx={{ fontSize: "1.1rem" }} />
                                        New Address Book
                                    </button>

                                    <div style={S.searchWrap}>
                                        <SearchIcon style={S.searchIcon} />
                                        <input
                                            type="text"
                                            style={S.searchInput}
                                            placeholder="Search books…"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onFocus={(e) => e.target.style.borderColor = "#1976d2"}
                                            onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
                                        />
                                    </div>
                                </div>

                                {loading ? (
                                    <div style={{ textAlign: "center", padding: "60px 0", color: "#aaa" }}>
                                        <i className="fa fa-spinner fa-spin" style={{ fontSize: 28, color: "#1976d2" }} />
                                        <p style={{ marginTop: 12 }}>Loading address books…</p>
                                    </div>
                                ) : filteredBooks.length === 0 ? (
                                    <div style={S.emptyState}>
                                        <PeopleAltOutlinedIcon sx={{ fontSize: "3.5rem", color: "#ddd", display: "block", margin: "0 auto 12px" }} />
                                        <p style={{ fontSize: 15, marginBottom: 6 }}>
                                            {searchQuery ? `No books match "${searchQuery}"` : "No address books yet"}
                                        </p>
                                        {!searchQuery && (
                                            <p style={{ fontSize: 13 }}>Create your first address book to get started.</p>
                                        )}
                                    </div>
                                ) : (
                                    <div style={S.grid}>
                                        {filteredBooks.map((book) => (
                                            <BookCard
                                                key={book.id}
                                                book={book}
                                                hovered={hoveredCard === book.id}
                                                onMouseEnter={() => setHoveredCard(book.id)}
                                                onMouseLeave={() => setHoveredCard(null)}
                                                onOpen={() => setSelectedBook(book)}
                                                onDelete={() => setDeletingBook(book)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Add book modal */}
            {showAddModal && (
                <AddBookModal
                    orgId={org?.id}
                    onClose={() => setShowAddModal(false)}
                    onCreated={handleBookCreated}
                />
            )}

            {/* Delete confirmation */}
            {deletingBook && (
                <div style={S.overlay} onClick={() => !deleting && setDeletingBook(null)}>
                    <div style={S.deleteModal} onClick={(e) => e.stopPropagation()}>
                        <div style={S.deleteModalHeader}>
                            <p style={S.deleteModalTitle}>Delete Address Book</p>
                            <button style={S.deleteModalClose} onClick={() => setDeletingBook(null)} disabled={deleting}>×</button>
                        </div>
                        <div style={S.deleteModalBody}>
                            <p style={S.deleteModalBodyText}>
                                Are you sure you want to delete <strong>"{deletingBook.name}"</strong>?
                            </p>
                            <div style={S.deleteModalWarn}>
                                All contacts in this book will be permanently removed and cannot be recovered.
                            </div>
                        </div>
                        <div style={S.deleteModalFooter}>
                            <button type="button" className="btn btn-secondary" onClick={() => setDeletingBook(null)} disabled={deleting}>
                                Cancel
                            </button>
                            <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={deleting}>
                                {deleting ? "Deleting…" : "Delete Book"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* POC: add-contacts QR / link modal */}
            {showInviteModal && (
                <div style={S.overlay} onClick={() => setShowInviteModal(false)}>
                    <div style={S.deleteModal} onClick={(e) => e.stopPropagation()}>
                        <div style={S.deleteModalHeader}>
                            <p style={S.deleteModalTitle}>Add-contacts link</p>
                            <button style={S.deleteModalClose} onClick={() => setShowInviteModal(false)}>×</button>
                        </div>
                        <div style={{ ...S.deleteModalBody, textAlign: "center" }}>
                            {inviteLoading ? (
                                <p style={{ color: "#777" }}>Generating…</p>
                            ) : inviteUrl ? (
                                <>
                                    <p style={{ fontSize: 13, color: "#555", marginBottom: 14 }}>
                                        Scan on an Android phone, or copy the link. Contacts go
                                        into this organisation's contact pool.
                                    </p>
                                    <img
                                        alt="QR code"
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(inviteUrl)}`}
                                        style={{ width: 220, height: 220, margin: "0 auto 14px", display: "block" }}
                                    />
                                    <input
                                        readOnly
                                        value={inviteUrl}
                                        onFocus={(e) => e.target.select()}
                                        style={{
                                            width: "100%", padding: "8px 10px",
                                            border: "1px solid #ddd", borderRadius: 6,
                                            fontSize: 12, marginBottom: 10,
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            navigator.clipboard.writeText(inviteUrl);
                                            toast.success("Link copied");
                                        }}
                                    >
                                        Copy link
                                    </button>
                                </>
                            ) : (
                                <p style={{ color: "#b91c1c" }}>Could not generate link.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// BookCard
// ---------------------------------------------------------------------------

function BookCard({ book, hovered, onMouseEnter, onMouseLeave, onOpen, onDelete }) {
    return (
        <div
            style={{
                ...S.card,
                boxShadow: hovered ? "0 4px 20px rgba(0,0,0,0.10)" : "0 1px 4px rgba(0,0,0,0.05)",
                borderColor: hovered ? "#1976d2" : "#e8eaf0",
            }}
            onClick={onOpen}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={S.cardIconWrap}>
                    <PeopleAltOutlinedIcon sx={{ fontSize: "1.5rem", color: "#1976d2" }} />
                </div>
                <button
                    style={{
                        ...S.cardDeleteBtn,
                        color: "#ef4444",
                        background: hovered ? "#fef2f2" : "transparent",
                    }}
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    title="Delete book"
                    onMouseEnter={(e) => e.currentTarget.style.background = "#fef2f2"}
                    onMouseLeave={(e) => e.currentTarget.style.background = hovered ? "#fef2f2" : "transparent"}
                >
                    <DeleteOutlineIcon sx={{ fontSize: "1.1rem" }} />
                </button>
            </div>

            <span style={S.cardName}>{book.name}</span>

            <span style={{ fontSize: 12, color: "#aaa" }}>
                Updated {formatDate(book.updatedAt)}
            </span>
        </div>
    );
}

export default OrgAddressBooks;
